import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { useId } from "react";

import {
  ITEM_TYPE_LABELS,
  type BackgroundItemType,
} from "@/api/backgroundVocabulary";
import type { BackgroundItemView } from "@/lib/backgroundDomains";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Input, Select, Textarea } from "@/ui/Input";

/**
 * One prompt's conditions, extraction settings and quality examples (#913).
 *
 * ## WHY IT IS INSIDE THE ROW
 *
 * The S7 contract splits the BG domain surface across two leaves in one
 * sentence: "#859 owns named domain/step editing; #913 adds the
 * conditional/extraction/quality controls to the same rows, not another
 * editor". `BackgroundItemRows` published the slot for exactly this and keyed it
 * on the item's stable id, so a reorder cannot move one prompt's gates onto
 * another's. This component is what fills it.
 *
 * ## WHY IT IS A DISCLOSURE
 *
 * The Lovable reference is a list of prompts and nothing else, and the ratified
 * obligation is to retain that screen rather than replace it. Eight prompts each
 * showing seven fields and a table of examples is a different screen. Closed by
 * default, the row is the reference's row; opened, it is this ticket's addition
 * — and the summary line says what is inside before it is opened, so nothing is
 * hidden, only folded. `<details>` is the application's own disclosure (the
 * validation panel, the project journey, the publication lane): it is operable
 * from the keyboard, announced as expandable, and needs no state.
 *
 * ## NOTHING IN HERE IS PER-LANGUAGE
 *
 * Every field this component renders is read by the runtime from the ANCHOR
 * document. `init._merge_lang_configs` builds a domain from `dict(he_cfg)` and
 * then replaces only `display_name`, `opening_prompt`, `first_question` and each
 * item's `description` — so an item's type, gates, registration key, extraction
 * note, trigger note and quality examples are the anchor's, whatever prompt
 * language the row above is showing.
 *
 * This comment used to say the prose half was written into the document being
 * edited. That was the assumption behind a real defect: with a non-anchor
 * language selected, those edits published successfully and changed nothing the
 * runtime reads. `shownLanguage` exists because of it — when the prompt above is
 * not the anchor's, the row SAYS which document these controls change, since a
 * comment and a `lang` attribute are not something a clinician can read.
 */

export interface BackgroundItemConditionsProps {
  item: BackgroundItemView;
  /** The ANCHOR's language — everything in here is stored there. */
  language: string | null;
  /**
   * The language whose PROMPT TEXT is on screen, when it is not the anchor's.
   *
   * `null` when they are the same, which is every monolingual domain. When they
   * differ the row says so out loud: the prompt above this disclosure is
   * English and everything inside it is the Hebrew the runtime reads, and a
   * clinician who believed otherwise would be overwriting live configuration
   * while thinking they were writing a translation.
   */
  shownLanguage: string | null;
  /** The item types the service admits, in its own order. */
  itemTypes: readonly BackgroundItemType[];
  readOnly: boolean;
  onChangeType: (value: string) => void;
  onChangePatientInfoField: (value: string) => void;
  onChangeFlag: (
    field: "critical_gate" | "binary_gate",
    value: boolean | null,
  ) => void;
  onChangeProse: (field: "extraction_note" | "trigger", value: string) => void;
  onChangeExample: (
    index: number,
    field: "value" | "reason" | "sufficient",
    value: string | boolean,
  ) => void;
  onAddExample: () => void;
  onRemoveExample: (index: number) => void;
  onMoveExample: (index: number, to: number) => void;
}

export function BackgroundItemConditions({
  item,
  language,
  shownLanguage,
  itemTypes,
  readOnly,
  onChangeType,
  onChangePatientInfoField,
  onChangeFlag,
  onChangeProse,
  onChangeExample,
  onAddExample,
  onRemoveExample,
  onMoveExample,
}: BackgroundItemConditionsProps) {
  const examples = item.qualityExamples ?? [];

  return (
    <details className="mt-2 rounded-md border border-border bg-background">
      <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground marker:text-muted-foreground">
        {/* The ITEM'S OWN ID in the disclosure's name, for the reason every
            reorder control carries it: a page of eight identical "Conditions
            and extraction" toggles is the repeated-ambiguous-control defect. */}
        Conditions and extraction — <code dir="ltr">{item.id}</code>
        {summarise(item) ? ` · ${summarise(item)}` : ""}
      </summary>

      <div className="space-y-4 border-t border-border px-3 py-3">
        {/* THE SCOPE, VISIBLE. A source comment and a `lang` attribute are not
            something a clinician can read. `_merge_lang_configs` takes every
            field below from the anchor document and replaces only the prompt
            text per language, so when the two differ the row has to say which
            document it is about to change. */}
        {shownLanguage !== null ? (
          <p className="rounded-md bg-muted px-3 py-2 text-xs text-foreground">
            The prompt above is the{" "}
            <code dir="ltr">{shownLanguage}</code> text. Everything below is the
            domain&apos;s runtime configuration, stored once in its{" "}
            <code dir="ltr">{language ?? "anchor"}</code> document and read for
            every language — editing it here changes what the interview does, not
            a translation.
          </p>
        ) : null}
        <div className="grid gap-3 @2xl:grid-cols-2">
          <LabelledSelect
            label={`When it is asked — ${item.id}`}
            description="A conditional prompt waits for a rule below to activate it."
            value={item.type}
            readOnly={readOnly}
            onChange={onChangeType}
          >
            {/* The STORED value first when it is not one the service admits, so
                a legacy document shows what it holds instead of silently
                presenting itself as `required`. */}
            {itemTypes.some((type) => type === item.type) ? null : (
              <option value={item.type}>{item.type || "(not set)"}</option>
            )}
            {itemTypes.map((type) => (
              <option key={type} value={type}>
                {ITEM_TYPE_LABELS[type]}
              </option>
            ))}
          </LabelledSelect>

          <LabelledInput
            label={`Patient info field — ${item.id}`}
            description="The registration key that pre-fills this prompt from what the Project already knows. Leave empty when nothing pre-fills it."
            value={item.patientInfoField}
            readOnly={readOnly}
            onChange={onChangePatientInfoField}
          />

          <GateSelect
            label={`Binary gate — ${item.id}`}
            description="A yes/no prompt whose answer rules below key on."
            value={item.binaryGate}
            readOnly={readOnly}
            onChange={(value) => onChangeFlag("binary_gate", value)}
          />

          <GateSelect
            label={`Critical gate — ${item.id}`}
            description="A prompt the domain will not be considered complete without."
            value={item.criticalGate}
            readOnly={readOnly}
            onChange={(value) => onChangeFlag("critical_gate", value)}
          />
        </div>

        <LabelledTextarea
          label={`Extraction note — ${item.id}`}
          description={
            shownLanguage === null
              ? "Guidance for the model reading the patient's answer: what counts as answered, and what to capture."
              : `Guidance for the model reading the patient's answer. Stored in the ${language ?? "anchor"} document and read for every language.`
          }
          value={item.extractionNote}
          language={language}
          rows={4}
          readOnly={readOnly}
          onChange={(value) => onChangeProse("extraction_note", value)}
        />

        <LabelledTextarea
          label={`Trigger note — ${item.id}`}
          description="Free prose describing when this conditional prompt applies. The deterministic half lives in the domain rules below."
          value={item.trigger}
          language={language}
          rows={2}
          readOnly={readOnly}
          onChange={(value) => onChangeProse("trigger", value)}
        />

        <section className="space-y-2">
          {/* `h3` under the Prompts card's own `h2` (`CardTitle`): the
              disclosure is not a heading, so an `h4` here skipped a level and
              axe was right to say so. */}
          <h3 className="text-sm font-medium text-foreground">
            Quality examples — <code dir="ltr">{item.id}</code>
          </h3>
          <p className="text-xs text-muted-foreground">
            Answers this prompt has already seen, each marked sufficient or not,
            with the reason. They teach the model where the line is; the
            insufficient ones carry most of it.
          </p>

          {examples.length === 0 ? (
            <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
              No quality examples. This prompt is judged on the extraction note
              alone.
            </p>
          ) : (
            <ol
              aria-label={`Quality examples for ${item.id}`}
              className="space-y-2"
            >
              {examples.map((example, index) => (
                <li
                  // POSITION, not value: an example has no id of its own, two
                  // may legitimately be identical mid-edit, and the list is
                  // ordered — so the index IS the identity here, unlike an item.
                  key={index}
                  className="rounded-md border border-border bg-card p-2"
                >
                  <div className="flex flex-wrap items-start gap-2">
                    <span className="w-5 shrink-0 pt-2 text-xs tabular-nums text-muted-foreground">
                      {index + 1}.
                    </span>
                    <div className="min-w-0 grow basis-56 space-y-2">
                      <LabelledTextarea
                        label={`Example ${index + 1} answer — ${item.id}`}
                        value={example.value}
                        language={language}
                        rows={2}
                        readOnly={readOnly}
                        onChange={(value) =>
                          onChangeExample(index, "value", value)
                        }
                      />
                      <LabelledSelect
                        label={`Example ${index + 1} verdict — ${item.id}`}
                        value={
                          example.sufficient === null
                            ? ""
                            : String(example.sufficient)
                        }
                        readOnly={readOnly}
                        onChange={(value) => {
                          // The blank option below is a stored value this build
                          // could not read as a verdict. Choosing it must not
                          // silently write `false` — a verdict is what the
                          // example teaches, and `false` is the half that
                          // carries most of the clinical meaning.
                          if (value !== "") {
                            onChangeExample(index, "sufficient", value === "true");
                          }
                        }}
                      >
                        {/* No absent option: `sufficient` is REQUIRED by the
                            schema, so "not set" is not a document a clinician
                            may reach from here. A stored value that is not a
                            boolean shows as its own blank option and is refused
                            by the service until it is answered. */}
                        {example.sufficient === null ? (
                          <option value="">(not a verdict)</option>
                        ) : null}
                        <option value="true">Sufficient</option>
                        <option value="false">Not sufficient</option>
                      </LabelledSelect>
                      <LabelledTextarea
                        label={`Example ${index + 1} reason — ${item.id}`}
                        value={example.reason}
                        language={language}
                        rows={2}
                        readOnly={readOnly}
                        onChange={(value) =>
                          onChangeExample(index, "reason", value)
                        }
                      />
                    </div>
                    {readOnly ? null : (
                      <div className="flex items-center gap-0.5 pt-1">
                        <IconButton
                          label={`Move example ${index + 1} of ${item.id} up`}
                          icon={<ArrowUp className="h-4 w-4" />}
                          disabled={index === 0}
                          onClick={() => onMoveExample(index, index - 1)}
                        />
                        <IconButton
                          label={`Move example ${index + 1} of ${item.id} down`}
                          icon={<ArrowDown className="h-4 w-4" />}
                          disabled={index === examples.length - 1}
                          onClick={() => onMoveExample(index, index + 1)}
                        />
                        <IconButton
                          label={`Remove example ${index + 1} of ${item.id}`}
                          icon={<Trash2 className="h-4 w-4" />}
                          onClick={() => onRemoveExample(index)}
                        />
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}

          {readOnly ? null : (
            <Button variant="outline" size="sm" onClick={onAddExample}>
              Add quality example to {item.id}
            </Button>
          )}
        </section>
      </div>
    </details>
  );
}

/** What the closed row says it is holding, or `""`. */
function summarise(item: BackgroundItemView): string {
  const held: string[] = [];
  const examples = item.qualityExamples?.length ?? 0;
  if (examples > 0) {
    held.push(`${examples} ${examples === 1 ? "example" : "examples"}`);
  }
  if (item.extractionNote !== "") {
    held.push("extraction note");
  }
  if (item.trigger !== "") {
    held.push("trigger note");
  }
  if (item.binaryGate !== null || item.criticalGate !== null) {
    held.push("gates");
  }
  if (item.patientInfoField !== "") {
    held.push("pre-fill");
  }
  return held.join(", ");
}

/**
 * The gate control, as THREE options.
 *
 * "Not set" is not decoration and not the same as "No". The engine reads an
 * absent `binary_gate` and an explicit `false` identically today, but the
 * document is what a clinician publishes and what an export is compared byte for
 * byte against — so a two-state switch would silently write `binary_gate: false`
 * into every source prompt that simply omits the key, and every equivalence
 * comparison would then report a difference this editor invented.
 */
function GateSelect({
  label,
  description,
  value,
  readOnly,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean | null;
  readOnly: boolean;
  onChange: (value: boolean | null) => void;
}) {
  return (
    <LabelledSelect
      label={label}
      description={description}
      value={value === null ? "" : String(value)}
      readOnly={readOnly}
      onChange={(next) => onChange(next === "" ? null : next === "true")}
    >
      <option value="">Not set</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </LabelledSelect>
  );
}

/**
 * The three primitives below exist because `ui/Input.Field` mints its own id and
 * this file needs the SAME label/description wiring on a control it also has to
 * name per item. They are local on purpose: they add no behaviour, only the
 * `htmlFor`/`aria-describedby` pair, and a fourth shared field abstraction is
 * what `ui/README.md` asks contributors not to introduce.
 */
function LabelledSelect({
  label,
  description,
  value,
  readOnly,
  onChange,
  children,
}: {
  label: string;
  description?: string;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  const inputId = useId();
  const descriptionId = useId();
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <Select
        id={inputId}
        {...(description ? { "aria-describedby": descriptionId } : {})}
        value={value}
        disabled={readOnly}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        {children}
      </Select>
      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function LabelledInput({
  label,
  description,
  value,
  readOnly,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <Input
        id={inputId}
        dir="ltr"
        aria-describedby={descriptionId}
        value={value}
        readOnly={readOnly}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      <p id={descriptionId} className="text-xs text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

function LabelledTextarea({
  label,
  description,
  value,
  language,
  rows,
  readOnly,
  onChange,
}: {
  label: string;
  description?: string;
  value: string;
  language: string | null;
  rows: number;
  readOnly: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <Textarea
        id={inputId}
        rows={rows}
        {...(description ? { "aria-describedby": descriptionId } : {})}
        value={value}
        readOnly={readOnly}
        dir="auto"
        {...(language ? { lang: language } : {})}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}
